// Plan 206 Part A — detect PDF citations in AgentBro assistant text and surface
// them as clickable chips that open the source PDF at the cited page in the side
// viewer. Frontend-only: no backend/agent change required.
//
// Primary trigger (no agent change): a URL pointing at a PDF. Our corpus stores
// R2 object keys as "<name>.pdf_<timestamp>", so ".pdf" is NOT at the end of the
// URL — match ".pdf" optionally followed by "_<digits>" and an optional ?query/#hash.
//   e.g. https://pub-xxx.r2.dev/pdfs/ambeepha_quarterly_2025_q2.pdf_20260307193405
// Secondary: the legacy inline form "[SOURCE, Title, Page, r2_url]".

// A PDF URL anywhere in prose. Stops at whitespace, angle/round/square brackets.
const PDF_URL_RE = /https?:\/\/[^\s<>()[\]]+?\.pdf(?:_\d+)?(?:[?#][^\s<>()[\]]*)?/gi;

// Legacy citation token: [SOURCE, Title, Page, r2_url]  (optionally wrapped in **).
const LEGACY_RE = /(?:\*\*)?\[SOURCE,\s*([^,\]]+),\s*([^,\]]+),\s*([^\]]+)\](?:\*\*)?/gi;

function prettyTitleFromUrl(url) {
  let base;
  try { base = decodeURIComponent(new URL(url).pathname.split('/').pop() || ''); }
  catch { base = url.split('/').pop() || url; }
  base = base
    .replace(/[?#].*$/, '')          // drop query/hash
    .replace(/\.pdf(_\d+)?$/i, '')   // drop ".pdf" / ".pdf_<ts>"
    .replace(/_null\b/gi, '')        // corpus keys carry a "_null" placeholder
    .replace(/[._]+/g, ' ')
    .trim();
  return base ? base.replace(/\b\w/g, (c) => c.toUpperCase()) : 'PDF';
}

function pageFromUrl(url) {
  const m = url.match(/[#&]page=(\d+)/i);
  return m ? parseInt(m[1], 10) : 1;
}

// PDFViewer appends its own "#page=…", so cite the bare object URL.
function stripHash(url) { return url.replace(/#.*$/, ''); }

// Parse assistant text → { citations: [{url, page, title}], cleanedText }.
// cleanedText has legacy [SOURCE,…] tokens removed (they are render artifacts, not
// prose); bare PDF URLs are left in place (the chip is additive, the URL still reads
// fine and stays clickable as a fallback).
export function extractPdfCitations(text) {
  if (!text || typeof text !== 'string') return { citations: [], cleanedText: text || '' };

  const citations = [];
  const seen = new Set();
  const add = (rawUrl, page, title) => {
    const url = stripHash(String(rawUrl).trim());
    if (!url) return;
    const pg = page || 1;
    const key = `${url}|${pg}`;
    if (seen.has(key)) return;
    seen.add(key);
    citations.push({ url, page: pg, title: title || prettyTitleFromUrl(url) });
  };

  // 1) Legacy bracket tokens — capture, then strip from the prose the user reads.
  const cleanedText = text.replace(LEGACY_RE, (_full, title, pageArg, url) => {
    const pm = String(pageArg).match(/(\d+)/);
    add(url, pm ? parseInt(pm[1], 10) : 1, String(title).trim());
    return '';
  });

  // 2) Bare PDF URLs in the REMAINING text (so a URL inside a legacy token isn't
  //    double-counted at a different page).
  for (const m of cleanedText.matchAll(PDF_URL_RE)) add(m[0], pageFromUrl(m[0]));

  return {
    citations,
    cleanedText: cleanedText
      .replace(/[ \t]+([.,;:!?])/g, '$1') // tidy orphan space left before punctuation by token removal
      .replace(/[ \t]{2,}/g, ' ')
      .trimEnd(),
  };
}

// True if a URL points at a PDF — lets LinkPreviews skip them (they render as a
// citation chip, not a generic link-preview card).
export function isPdfUrl(url) {
  return /\.pdf(_\d+)?($|[?#])/i.test(url || '');
}
